import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import type { ClientNotification } from '@weft/types';
import { NOTIFICATION_EVENT_TYPES, type NotificationEventType } from '@weft/types';
import { dayjs, raiseUnreachable } from '@weft/utils';
import { formatDurationBetweenDates } from 'apps/app/src/utils/date';
import { useVyNavigate } from '../../../../../hooks/use-vy-navigate';

type HomePageSidebarNotificationItemProps = {
  clientNotification: ClientNotification;
  onDismiss: (clientNotification: ClientNotification) => void;
};

export const HomePageSidebarNotificationItem: React.FC<HomePageSidebarNotificationItemProps> = ({ clientNotification: { notification, notificationAlert }, onDismiss }) => {
  const today = dayjs();
  const navigate = useVyNavigate();
  const navigationPath = getNotificationNavigationPath(notification.eventType, notification.entity.entityId);

  return (
    <Box paddingY={1}>
      <Stack
        height="100%"
        gap={1}
      >
        <Stack
          justifyContent="space-between"
          alignItems="center"
          direction="row"
        >
          <Chip
            size='small'
            label={getNotificationLabel(notification.eventType)}
          />
          <Typography
            variant='body2'
            color={theme => theme.palette.text.secondary}
          >
            {formatDurationBetweenDates(notification.metadata.date, today)}
          </Typography>
        </Stack>
        <Typography
          paddingLeft={1}
          variant='body1'
        >
          {notification.message}
        </Typography>
        <Stack
          paddingTop={2}
          justifyContent="space-between"
          direction="row"
        >
          <Button
            onClick={() => navigate(navigationPath)}
            size='small'
            variant='outlined'
            color='primary'
            sx={{
              height: '30px',
              padding: '0px 10px !important',
            }}
          >
            {getNotificationActionLabel(notification.eventType)}
          </Button>
          <Button
            size='medium'
            color='primary'
            onClick={() => onDismiss?.({ notification, notificationAlert })}
          >
            Dismiss
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

function getNotificationNavigationPath(eventType: NotificationEventType, entityId: string): string {
  switch (eventType) {
    case NOTIFICATION_EVENT_TYPES.Overage:
      return `/invoices/overview/dynamic/${entityId}`;
    case NOTIFICATION_EVENT_TYPES.AnonymousCustomer:
      return `/customers/${entityId}`;
    case NOTIFICATION_EVENT_TYPES.UpcomingRenewal:
      return `/plans/contracts/${entityId}`;
    case NOTIFICATION_EVENT_TYPES.InvoiceApproved:
      return `/invoices/overview/light/${entityId}`;
    default:
      return raiseUnreachable(eventType);
  }
}

function getNotificationActionLabel(eventType: NotificationEventType): string {
  switch (eventType) {
    case NOTIFICATION_EVENT_TYPES.Overage:
      return 'Review Invoice';
    case NOTIFICATION_EVENT_TYPES.AnonymousCustomer:
      return 'Review Customer';
    case NOTIFICATION_EVENT_TYPES.UpcomingRenewal:
      return 'Review Contract';
    case NOTIFICATION_EVENT_TYPES.InvoiceApproved:
      return 'Review Invoice';
    default:
      return raiseUnreachable(eventType);
  }
}

function getNotificationLabel(eventType: NotificationEventType): string {
  switch (eventType) {
    case NOTIFICATION_EVENT_TYPES.Overage:
      return 'Usage Overage';
    case NOTIFICATION_EVENT_TYPES.AnonymousCustomer:
      return 'Customer Created';
    case NOTIFICATION_EVENT_TYPES.UpcomingRenewal:
      return 'Upcoming Renewal';
    case NOTIFICATION_EVENT_TYPES.InvoiceApproved:
      return 'Invoice Approved';
    default:
      return raiseUnreachable(eventType);
  }
}